import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { fetchMyPermissions } from '../api/role-permission-api';
import { useAuth } from './useAuth';
import { menuGroups, tabUrl } from '../router/menu';
import { agentRoles, otherRoles } from '../router/roles';

const PermissionsContext = createContext({
  permissions: [],
  role: null,
  loading: true,
  can: () => false,
  canAll: () => false,
  visibleMenuGroups: [],
  homeLink: '/',
  refresh: () => {},
});

/**
 * The signed-in user's permissions (GET /admin/roles-permissions/me), loaded once per login
 * and shared by the menu, the route guard and the pages.
 *
 * This is convenience only - the API enforces the same permissions server side, so a user
 * who forces a hidden screen open still gets a 403 from every request it makes.
 */
export const PermissionsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  // Permissions are tagged with the user they were loaded for. Loading is derived from that
  // rather than kept as a flag: after a reload, auth restores the user one render after this
  // provider first runs, and a flag would briefly say "done, no permissions" for that user,
  // sending them off every page they are allowed on.
  const [loaded, setLoaded] = useState({ userId: undefined, permissions: [] });

  const userId = user?._id || user?.id || null;
  const permissions = loaded.permissions;
  const loading = authLoading || loaded.userId !== userId;

  const load = useCallback(async () => {
    if (!userId) {
      setLoaded({ userId: null, permissions: [] });
      return;
    }
    const { data } = await fetchMyPermissions();
    setLoaded({ userId, permissions: data?.permissions ?? [] });
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(() => {
    const can = (permission) => permissions.includes(permission);
    const canAll = (required = []) => required.every(can);

    // Each tab lists the permissions it needs; a group with no visible tab is dropped.
    const visibleMenuGroups = menuGroups
      .map((group) => ({
        ...group,
        tabs: group.tabs.filter((tab) => canAll(tab.permissions)),
      }))
      .filter((group) => group.tabs.length > 0);

    // Mentors, organizations and agents keep their own home page even when granted admin
    // items; everyone else starts on their first visible menu item.
    const role = user?.role;
    const firstTab = visibleMenuGroups[0]?.tabs[0];
    let homeLink = '/';
    if (agentRoles.includes(role)) homeLink = '/agent-home';
    else if (otherRoles.includes(role)) homeLink = '/clinic-medic';
    else if (user) homeLink = firstTab ? tabUrl(firstTab) : '/profile';

    return {
      permissions,
      role: role ?? null,
      loading,
      can,
      canAll,
      visibleMenuGroups,
      homeLink,
      refresh: load,
    };
  }, [permissions, loading, user, load]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

PermissionsProvider.propTypes = {
  children: PropTypes.node,
};

export const usePermissions = () => useContext(PermissionsContext);
